import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  Button,
  Skeleton,
  Container,
  Collapse,
  IconButton,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ExpandMore, ExpandLess, ChevronRight } from "@mui/icons-material";
import LoadingPage from "../components/LoadingPage";
import SiteImage from "../components/SiteImage";

const ViewSites = ({ siteId }) => {
  const { accessToken, isAdmin, isSuperuser } = useAuth();
  const [allSites, setAllSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSitesOpen, setIsSitesOpen] = useState(true);
  const [filters, setFilters] = useState({
    id: "",
    name: "",
    site_name: "",
  });

  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASEURL;
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const sitesRes = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const fetchedSites = sitesRes.data.data || [];

        const siteDetailsPromises = fetchedSites.map((site) =>
          axios.get(`${baseURL}/sites/${site.id}/?with_image=true`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
        );
        const siteDetailsRes = await Promise.all(siteDetailsPromises);
        const siteDetails = siteDetailsRes.map((res) => res.data);

        const devicesRes = await axios.get(`${baseURL}/devices`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setDevices(devicesRes.data || []);

        setAllSites(siteDetails);
        setFilteredSites(siteDetails);
      } catch (error) {
        console.error(
          "Error fetching sites or devices:",
          error.response ? error.response.data : error.message
        );
        setError("Failed to load sites or devices. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accessToken]);

  // useEffect(() => {
  //   if (allSites.length > 0 && filters.site_name === "") {
  //     const sortedSites = [...allSites].sort((a, b) => {
  //       const nameA = a.name || `Site ${a.id}`;
  //       const nameB = b.name || `Site ${b.id}`;
  //       return nameA.localeCompare(nameB) || a.id - b.id;
  //     });
  //     const firstSite = sortedSites[0];
  //     setFilters({
  //       id: String(firstSite.id),
  //       name: "",
  //       site_name: firstSite.name || `Site ${firstSite.id}`,
  //     });
  //   }
  // }, [allSites]);

  useEffect(() => {
    const filtered = allSites.filter((site) => {
      const idMatch = filters.id
        ? String(site.id) === String(filters.id)
        : true;
      const nameMatch = filters.name
        ? (site.name || `Site ${site.id}`)
            ?.toLowerCase()
            .includes(filters.name.toLowerCase())
        : true;
      const siteNameMatch = filters.site_name
        ? (site.name || `Site ${site.id}`) === filters.site_name
        : true;

      return idMatch && nameMatch && siteNameMatch;
    });

    setFilteredSites(filtered);
  }, [filters, allSites]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };

      if (name === "id" && value) {
        const selectedSite = allSites.find(
          (site) => String(site.id) === String(value)
        );
        newFilters.site_name = selectedSite
          ? selectedSite.name || `Site ${selectedSite.id}`
          : "";
      } else if (name === "id" && !value) {
        newFilters.site_name = "";
      } else if (name === "site_name" && value) {
        const selectedSite = allSites.find(
          (site) => (site.name || `Site ${site.id}`) === value
        );
        newFilters.id = selectedSite ? String(selectedSite.id) : "";
      } else if (name === "site_name" && !value) {
        newFilters.id = "";
      }

      return newFilters;
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
  };

  const handleClearFilters = () => {
    setFilters({
      id: "",
      name: "",
      site_name: "",
    });
  };

  const LoadingSkeleton = () => (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
        <Typography variant="h4" gutterBottom>
          <Skeleton width="30%" height={40} />
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            <Skeleton width="20%" height={40} />
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            {Array(3)
              .fill()
              .map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={isXsScreen ? "100%" : 150}
                  height={56}
                />
              ))}
          </Box>
        </Box>
        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            <Skeleton width={isXsScreen ? "40%" : "20%"} height={56} />
          </Typography>
          <Grid container spacing={3}>
            {Array(3)
              .fill()
              .map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Skeleton
                    variant="rectangular"
                    sx={{ borderRadius: 2, aspectRatio: "4/3", height: 250 }}
                  />
                </Grid>
              ))}
          </Grid>
        </Paper>
      </Box>
    </Container>
  );

  if (loading) return <LoadingPage />;

  if (error)
    return (
      <Container maxWidth="xl">
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );

  const uniqueSiteIds = [...new Set(allSites.map((site) => String(site.id)))].sort(
    (a, b) => a - b
  );
  const uniqueSiteNames = [
    ...new Set(allSites.map((site) => site.name || `Site ${site.id}`)),
  ].sort();

  return (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}
          >
            Sites
          </Typography>
          <Box sx={{ display: "flex", gap: "1rem" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: "1rem"}}>
                {/* <Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate("/water-balance")}
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1rem" },
                      textTransform: "none",
                      color: "white"
                    }}
                  >
                    Water Saved
                  </Button>
                </Box> */}
              </Box>
            {(isAdmin || isSuperuser) && (
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: "1rem"}}>
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("sites/add")}
                  sx={{
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    textTransform: "none",
                  }}
                >
                  Add Site
                </Button>
              </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
          >
            Filter Sites
          </Typography>
          <Box
            component="form"
            onSubmit={handleFilterSubmit}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="Site Name"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
              InputLabelProps={{ style: { color: "black", fontWeight: 100 } }}
            />
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <InputLabel
                id="site-name-label"
                sx={{ color: "black", fontWeight: 100 }}
              >
                Site Name
              </InputLabel>
              <Select
                labelId="site-name-label"
                name="site_name"
                value={filters.site_name}
                onChange={handleFilterChange}
                label="Site Name"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {uniqueSiteNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <InputLabel
                id="site-id-label"
                sx={{ color: "black", fontWeight: 100 }}
              >
                Site ID
              </InputLabel>
              <Select
                labelId="site-id-label"
                name="id"
                value={filters.id}
                onChange={handleFilterChange}
                label="Site ID"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {uniqueSiteIds.map((id) => (
                  <MenuItem key={id} value={id}>
                    {id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 1,
              borderRadius: 1,
            }}
            onClick={() => setIsSitesOpen(!isSitesOpen)}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" }, flexGrow: 1 }}
            >
              Sites
            </Typography>
            <IconButton size="small">
              {isSitesOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isSitesOpen}>
            {filteredSites.length > 0 ? (
              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 3,
                }}
              >
                {filteredSites.map((site) => {
                  const siteDevices = devices.filter(
                    (device) => device.site === site.id
                  );
                  const groupIds = [
                    ...new Set(
                      siteDevices.flatMap((device) => device.groups || [])
                    ),
                  ];
                  return (
                    <Box key={site.id} sx={{ minWidth: 0 }}>
                      <Card
                        sx={{
                          width: "100%",
                          height: "100%",
                          borderRadius: 2,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          transition: "box-shadow 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        <SiteImage
                          src={site.image}
                          alt={site.name || `Site ${site.id}`}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography
                              variant="h6"
                              sx={{ fontSize: { xs: "1rem", md: "1.1rem" }, mb: 1 }}
                            >
                              {site.name || `Site ${site.id}`}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            ID: {site.id}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Latitude: {site.latitude}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Longitude: {site.longitude}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Devices: {site.device_count}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            Groups: {groupIds.length > 0 ? groupIds.join(", ") : "N/A"}
                          </Typography>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "1rem", mt: 2 }}>
                              {(isAdmin || isSuperuser) && (
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => navigate(`/sites/edit-site/${site.id}`)}
                                  sx={{
                                    fontSize: { xs: "0.9rem", md: "1rem" },
                                    textTransform: "none",
                                    color: "white"
                                  }}
                                >
                                  Edit Site
                                </Button>
                              )}
                            <Box>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                  navigate("/dashboard/statistics", {
                                    state: { siteId: site.id !== "all" ? site.id : null },
                                  })
                                }
                                sx={{
                                  fontSize: { xs: "0.9rem", md: "1rem" },
                                  textTransform: "none",
                                }}
                              >
                                View Statistics
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                No sites found matching the filters.
              </Typography>
            )}
          </Collapse>
        </Paper>
      </Box>
    </Container>
  );
};

export default ViewSites;
